import { baseApi } from '@/lib/store/api/baseQuery'
import { apiEndpoints } from '@/lib/api/endpoints'
import type { PreVisitRequirement, PreVisitState } from '@/lib/store/api/preVisitRequirementsApi'
import type { CareActionItem, CareActionKind, VisitSummaryNextSteps } from '@/types/interfaces/careActions'

export type MarkUpdateReadArg =
    | { notificationId: string; notifiableId?: undefined }
    | { notifiableId: string; notificationId?: undefined }

/** Resolves the Updates read payload for one row. Check-in history is per notification id. */
export function resolveMarkUpdateReadArgs(action: CareActionItem): MarkUpdateReadArg | undefined {
    if (action.kind === 'medication_check_in' && action.id) {
        return { notificationId: action.id }
    }

    const notifiableId =
        action.event_type === 'care_plan_ready' ? action.care_plan_id : action.notifiable_id
    return notifiableId ? { notifiableId } : undefined
}

/** A single card in the Updates feed: one care object's latest status change. */
export interface UpdateItem {
    id: string
    notifiable_type: string
    /** Id of the backing care object, used to build the card's detail link. */
    notifiable_id: string
    /** The care object's own state/transition at emit time (raw). */
    event_type: string
    /** The care object's current live state, or null when it exposes none. */
    status: string | null
    /** Best-effort display label for the object (e.g. lab test names), or null. */
    title: string | null
    unread: boolean
    created_at: string
    /**
     * The care object's own kind + ids, sent when the backend can name a subtype
     * more precisely than `notifiable_type` can, so the row renders (icon, link)
     * identically to its My Care feed row. Absent for object-typed notifiables,
     * which derive kind from notifiable_type.
     */
    kind?: CareActionKind
    appointment_id?: string
    /** Catalog SKU backing a visit update, for the patient-facing visit label. */
    sku_id?: number | null
    sku_name?: string | null
    care_plan_id?: string
    /** The care plan's workflow state, independent of patient visibility. */
    care_plan_state?: string | null
    /** Backend-authoritative decision that the patient may open the care plan. */
    care_plan_visible?: boolean
    /** "Dr. Lastname"-style name for visit-update sentences. */
    provider_short_name?: string | null
    lab_order_id?: string
    care_id?: string
    care_action_id?: string
    in_person_visit_order_id?: string
    provider_name?: string | null
    /** Chat visit response deadline used by the booked update headline. */
    sla_promise?: string | null
    /** Short patient-facing lab brand for scheduled-lab update headlines. */
    lab_vendor_name?: string | null
    /** Care plan's categorized next-steps breakdown, for the visit-summary sentence. */
    next_steps?: VisitSummaryNextSteps | null
    /** ISO timestamp of the scheduled lab draw or imaging appointment, when known. */
    scheduled_at?: string | null
    /** End of a video visit slot on pre-visit requirement Updates rows. */
    scheduled_end_at?: string | null
    /** Incomplete pre-visit steps when event_type is pre_visit_requirements. */
    pre_visit_requirement_count?: number | null
    /** Live pre-visit requirement state for Updates rows. */
    pre_visit_status?: PreVisitState | null
    /** Visit due instant used with patient_timezone to format Today/Tomorrow/MMMM d. */
    due_at?: string | null
    /** Server-computed visit date label; the client reformats from due_at when present. */
    due_date_label?: string | null
    /** IANA timezone used to format due_at for Updates copy. */
    patient_timezone?: string | null
    /** Shared requirement payload for pre-visit Updates rows. */
    requirements?: {
        id_verification?: PreVisitRequirement
        health_history?: PreVisitRequirement
    }
    /** Imaging center name when the care action has a selected facility. */
    imaging_facility_name?: string | null
    /** Vital collection method / appointment state facts for lab updates. */
    walk_in?: boolean
    lab_appointment_status?: string | null
    /** Thin result counts; raw medical results are not part of Updates. */
    results_count?: number | null
    abnormal_results_count?: number | null
    /** Outstanding check-in steps when this row is a medication check-in. */
    pending_criteria_count?: number | null
    /** Specialty key on a video visit with specialist consultation. */
    econsult_specialty?: string | null
    /** Patient-facing specialty noun for titles and update headlines. */
    econsult_specialty_noun?: string | null
    /** Durable specialist-consult phase when the update is for an e-consult visit. */
    specialist_consult_phase?: 'summary_processing' | 'consulting' | 'review_complete' | null
    /** Updates carousel goto label when it should not be "View". */
    cta_label?: string | null
    /** Explicit detail link for notifiables whose route cannot be derived from an id alone (care recommendations). */
    href?: string | null
    /** Message Center conversation backing a `message` card; the card opens that thread. */
    conversation_id?: string
    /** Conversation topic shown as the `message` card's caption (e.g. "Neck strain visit"). */
    topic?: string | null
    /** Preview of the unread message body shown as the `message` card's headline (clamped to two lines). */
    preview?: string | null
    /** True when a `message` card's author is a non-provider agent ("your support team", no headshot). */
    from_support?: boolean
    /** Presigned headshot URL for a provider-authored `message` card. */
    headshot_image_url?: string | null
}

export interface UpdatesFeed {
    updates: UpdateItem[]
    unread_count: number
}

export interface UnreadCount {
    unread_count: number
}

export const updatesApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        getUpdates: builder.query<UpdatesFeed, void>({
            query: () => apiEndpoints.updates.list,
            // Requirement completion invalidates PreVisitRequirements, so visit-backed
            // Updates cards refresh in the same render and do not linger as stale rows.
            providesTags: ['Updates', 'PreVisitRequirements'],
        }),

        // Cheap badge query for the nav; separate tag so marking read can refresh
        // the badge without forcing the feed list to refetch.
        getUpdatesUnreadCount: builder.query<UnreadCount, void>({
            query: () => apiEndpoints.updates.unreadCount,
            providesTags: ['UpdatesUnreadCount'],
        }),

        /**
         * Settles unread Updates rows. Most kinds clear every unread event for one care
         * object; medication_check_in history clears a single notification id.
         */
        markObjectRead: builder.mutation<UnreadCount, MarkUpdateReadArg>({
            query: ({ notifiableId, notificationId }) => ({
                url: apiEndpoints.updates.read,
                method: 'POST',
                body: notificationId ? { notification_id: notificationId } : { notifiable_id: notifiableId },
                keepalive: true,
            }),
            async onQueryStarted({ notifiableId, notificationId }, { dispatch, queryFulfilled }) {
                const patch = dispatch(
                    updatesApi.util.updateQueryData('getUpdates', undefined, (draft) => {
                        if (notificationId) {
                            const row = draft.updates.find((item) => item.id === notificationId)
                            if (row?.unread) {
                                row.unread = false
                                draft.unread_count = Math.max(0, draft.unread_count - 1)
                            }
                            return
                        }

                        draft.updates.forEach((item) => {
                            // care_plan_ready is recorded on the visit but read only via
                            // the linked care-plan page. Mirror the backend's linked-page
                            // exception: viewing the visit must leave this row unread.
                            const matches =
                                item.event_type === 'care_plan_ready'
                                    ? item.care_plan_id === notifiableId
                                    : item.notifiable_id === notifiableId
                            if (matches && item.unread) {
                                item.unread = false
                                draft.unread_count = Math.max(0, draft.unread_count - 1)
                            }
                        })
                    }),
                )
                try {
                    const { data } = await queryFulfilled
                    dispatch(
                        updatesApi.util.updateQueryData('getUpdatesUnreadCount', undefined, (draft) => {
                            draft.unread_count = data.unread_count
                        }),
                    )
                } catch {
                    patch.undo()
                }
            },
        }),

        /**
         * Settles every unread event the customer has, backing the Updates header's
         * "Mark all as read". Deliberately explicit — nothing clears on view.
         */
        markAllUpdatesRead: builder.mutation<UnreadCount, void>({
            query: () => ({
                url: apiEndpoints.updates.readAll,
                method: 'POST',
            }),
            invalidatesTags: ['UpdatesUnreadCount'],
            // Patch the cached feed rather than invalidating it: the rows themselves
            // don't change, only their dots, so a full refetch buys nothing.
            async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
                const patch = dispatch(
                    updatesApi.util.updateQueryData('getUpdates', undefined, (draft) => {
                        draft.updates.forEach((item) => {
                            item.unread = false
                        })
                        draft.unread_count = 0
                    }),
                )
                try {
                    await queryFulfilled
                } catch {
                    patch.undo()
                }
            },
        }),
    }),
    overrideExisting: false,
})

export const {
    useGetUpdatesQuery,
    useGetUpdatesUnreadCountQuery,
    useMarkObjectReadMutation,
    useMarkAllUpdatesReadMutation,
} = updatesApi
